import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { Loader2, Check, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PageHeader({ title, subtitle, action, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8" data-testid={testId}>
      <div>
        <h1 className="text-4xl sm:text-5xl tracking-tight leading-tight text-primary">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-2 max-w-xl">{subtitle}</p>}
      </div>
      {action}
    </motion.div>
  );
}

export function SaveBadge({ status }) {
  if (status === "saving")
    return <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="save-status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</span>;
  if (status === "saved")
    return <span className="inline-flex items-center gap-1.5 text-xs text-primary" data-testid="save-status"><Check className="h-3.5 w-3.5" /> Saved</span>;
  if (status === "error")
    return <span className="inline-flex items-center gap-1.5 text-xs text-destructive" data-testid="save-status"><AlertTriangle className="h-3.5 w-3.5" /> Couldn't save. Retrying…</span>;
  return null;
}

export function EmptyState({ icon: Icon, title, description, actionLabel, onAction, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center text-center py-20 px-6 rounded-2xl bg-secondary/40 border border-border" data-testid={testId}>
      {Icon && (
        <div className="h-16 w-16 rounded-full bg-background flex items-center justify-center mb-5 border border-border">
          <Icon className="h-7 w-7 text-accent" strokeWidth={1.5} />
        </div>
      )}
      <h3 className="text-2xl tracking-tight text-primary">{title}</h3>
      <p className="text-muted-foreground mt-2 max-w-sm text-sm">{description}</p>
      {actionLabel && (
        <Button onClick={onAction} className="mt-6 rounded-full transition-transform hover:scale-[1.03]" data-testid={`${testId}-action`}>
          <Plus className="h-4 w-4 mr-1.5" /> {actionLabel}
        </Button>
      )}
    </motion.div>
  );
}

export function AnimatedNumber({ value, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, Number(value) || 0, {
      duration: 1.1, ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [inView, value, mv]);

  return <span ref={ref} className={className}>{display.toLocaleString()}</span>;
}

export function StatCard({ label, value, sub, icon: Icon, accent, testId, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -4 }}
      className="rounded-2xl bg-card border border-border p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-shadow" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-accent" strokeWidth={1.5} />}
      </div>
      <div className={`mt-3 text-3xl tnum font-semibold ${accent ? "text-accent" : "text-primary"}`}>{value}</div>
      {sub && <div className="text-sm text-muted-foreground mt-1">{sub}</div>}
    </motion.div>
  );
}

export function Progress({ value, className = "" }) {
  return (
    <div className={`h-2 rounded-full bg-secondary overflow-hidden ${className}`}>
      <motion.div className="h-full bg-accent rounded-full"
        initial={{ width: 0 }} animate={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }} />
    </div>
  );
}

export function FadeIn({ children, delay = 0, className = "" }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }} className={className}>
      {children}
    </motion.div>
  );
}

export function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-10 w-64 bg-secondary rounded-lg" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 bg-secondary rounded-2xl" />)}
      </div>
      <div className="h-72 bg-secondary rounded-2xl" />
    </div>
  );
}
